import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import StatusIndicatorBar from '../../components/ui/StatusIndicatorBar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import UploadZone from './components/UploadZone';
import ProcessingStages from './components/ProcessingStages';
import ColumnPreview from './components/ColumnPreview';
import UploadHistory from './components/UploadHistory';
import ValidationResults from './components/ValidationResults';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';




const CSVImportDashboard = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [detectedColumns, setDetectedColumns] = useState([]);
  const [sampleData, setSampleData] = useState([]);
  const [validationResults, setValidationResults] = useState({
    errors: [],
    warnings: [],
    info: []
  });
  const [showColumnPreview, setShowColumnPreview] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const [uploadHistory] = useState([]);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setIsProcessing(true);
    setCurrentStage('validation');
    setProgress(0);
    setShowColumnPreview(false);
    setShowValidation(false);

    window.dispatchEvent(new CustomEvent('statusUpdate', {
      detail: {
        type: 'processing',
        message: 'Iniciando processamento do arquivo...',
        progress: 0,
        details: `Arquivo: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`
      }
    }));

    processCSVFile(file);
  };

  const processCSVFile = async (file) => {
    try {
      // Use new CSV import service with batch tracking
      const { csvImportService } = await import('../../services/csvImportService');
      
      await csvImportService?.importCSVFile(file, (progressData) => {
        // Update UI based on stage
        const stageMap = {
          'reading': { stage: 'validation', progress: 10, message: 'Lendo arquivo CSV...' },
          'parsing': { stage: 'normalization', progress: 30, message: 'Analisando dados...' },
          'batch': { stage: 'mapping', progress: 40, message: 'Criando lote de importação...' },
          'mapping': { stage: 'mapping', progress: 50, message: 'Mapeando produtos...' },
          'saving': { stage: 'database', progress: 70, message: progressData?.message },
          'complete': { stage: 'complete', progress: 100, message: 'Importação concluída!' }
        };
        
        const uiUpdate = stageMap?.[progressData?.stage] || progressData;
        setCurrentStage(uiUpdate?.stage);
        setProgress(uiUpdate?.progress);
        
        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: uiUpdate.stage === 'complete' ? 'success' : 'processing',
            message: uiUpdate.message,
            progress: uiUpdate.progress,
            details: progressData.message
          }
        }));
      });
      
      // Import complete
      setCurrentStage('complete');
      setProgress(100);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: 'Importação concluída com sucesso!',
          progress: 100,
          details: 'Produtos importados com rastreamento CSV'
        }
      }));

      setTimeout(() => {
        setIsProcessing(false);
        setCurrentStage(null);
      }, 2000);
    } catch (error) {
      console.error('Error processing CSV:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao processar arquivo CSV',
          progress: 0,
          details: error.message
        }
      }));
      setIsProcessing(false);
    }
  };

  const handleViewCatalog = () => {
    navigate('/product-catalog-manager');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <StatusIndicatorBar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <BreadcrumbNavigation />

          <div className="mb-6 md:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                  Importação de Produtos
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Faça upload do seu catálogo WeDrop em formato CSV para processamento inteligente
                </p>
              </div>

              {!isProcessing && selectedFile && (
                <Button
                  variant="default"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={handleViewCatalog}
                >
                  Ir para Catálogo
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <UploadZone 
                onFileSelect={handleFileSelect}
                isProcessing={isProcessing}
              />

              {isProcessing && (
                <ProcessingStages 
                  currentStage={currentStage}
                  progress={progress}
                />
              )}

              {showColumnPreview && (
                <ColumnPreview 
                  detectedColumns={detectedColumns}
                  sampleData={sampleData}
                />
              )}

              {showValidation && (
                <ValidationResults results={validationResults} />
              )}

              {!isProcessing && !selectedFile && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 flex-shrink-0">
                      <Icon name="Lightbulb" size={24} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground mb-2">
                        Dicas para uma Importação Perfeita
                      </h4>
                      <ul className="space-y-2 caption text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                          <span>Use o template fornecido para garantir compatibilidade total</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                          <span>Preços podem estar em formato "R$ 29,90" - serão normalizados automaticamente</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                          <span>A IA detectará variações automaticamente baseada em SKU e nomes similares</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                          <span>Campos vazios em custo serão tratados como R$ 0,00 sem bloquear a importação</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <UploadHistory history={uploadHistory} onRefresh={() => window.location?.reload()} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CSVImportDashboard;